
import React from 'react';
import { NavLink, useLocation, useNavigate } from 'react-router-dom';
import { UserRole } from '../../types';
import { LayoutGrid, ArrowRightLeft, Landmark, FileBarChart, Users2, ShieldCheck, Zap } from 'lucide-react';
import { LogoIcon } from '../common/Logo';

interface BottomNavProps {
  role: UserRole;
}

const BottomNav: React.FC<BottomNavProps> = ({ role }) => {
  const location = useLocation();
  const navigate = useNavigate();
  
  const userLinks = [
    { label: 'Dash', path: '/user/dashboard', icon: <LayoutGrid size={20} /> },
    { label: 'Trade', path: '/user/trades', icon: <ArrowRightLeft size={20} /> },
    { label: 'Funds', path: '/user/withdrawals', icon: <Landmark size={20} /> },
    { label: 'Stats', path: '/user/reports', icon: <FileBarChart size={20} /> },
  ];

  const adminLinks = [
    { label: 'Core', path: '/admin/dashboard', icon: <LayoutGrid size={20} /> },
    { label: 'Users', path: '/admin/users', icon: <Users2 size={20} /> },
    { label: 'KYC', path: '/admin/kyc', icon: <ShieldCheck size={20} /> },
    { label: 'Engine', path: '/admin/trading', icon: <Zap size={20} /> },
  ];

  const links = role === UserRole.ADMIN ? adminLinks : userLinks;
  
  // Split links for center logo layout
  const mid = 2;
  const leftLinks = links.slice(0, mid);
  const rightLinks = links.slice(mid);

  const renderLink = (link: typeof links[0]) => {
    const isActive = location.pathname.includes(link.path);
    return (
      <NavLink
        key={link.path}
        to={link.path}
        className={`
          flex flex-col items-center gap-1.5 transition-all duration-300 relative px-2
          ${isActive ? 'text-brand-primary scale-110' : 'text-brand-muted opacity-60'}
        `}
      >
        <span className={`transition-transform duration-300 ${isActive ? 'scale-110' : ''}`}>
          {link.icon}
        </span>
        <span className="text-[7px] font-black uppercase tracking-widest">{link.label}</span>
        
        {isActive && (
          <div className="absolute -top-1 w-1 h-1 rounded-full bg-brand-primary shadow-[0_0_10px_#FFCE1B]"></div>
        )}
      </NavLink>
    );
  };

  const handleLogoClick = () => {
    const dashboardPath = role === UserRole.ADMIN ? '/admin/dashboard' : '/user/dashboard';
    navigate(dashboardPath);
  };

  return (
    <nav className="md:hidden fixed bottom-0 left-0 right-0 h-20 glass-nav border-t border-white/5 flex items-center justify-between px-6 pb-safe shadow-[0_-15px_40px_rgba(0,0,0,0.6)] z-50">
      {/* Left Menu Items */}
      <div className="flex items-center justify-between flex-1">
        {leftLinks.map(renderLink)}
      </div>

      {/* Floating Center Logo */}
      <div className="flex-shrink-0 px-4 relative">
        <button 
          onClick={handleLogoClick}
          className="w-16 h-16 bg-brand-primary rounded-full flex items-center justify-center shadow-[0_0_30px_rgba(255,206,27,0.4)] border-4 border-brand-dark -translate-y-6 transform transition-transform duration-500 active:scale-90 active:rotate-12 group"
        >
          <div className="group-hover:scale-110 transition-transform duration-500">
            <LogoIcon width={32} height={32} />
          </div>
          {/* Subtle indicator if on dashboard */}
          {(location.pathname === '/admin/dashboard' || location.pathname === '/user/dashboard') && (
             <div className="absolute -bottom-1 w-1.5 h-1.5 bg-black rounded-full shadow-[0_0_8px_rgba(0,0,0,0.5)]"></div>
          )}
        </button>
      </div>

      {/* Right Menu Items */}
      <div className="flex items-center justify-between flex-1">
        {rightLinks.map(renderLink)}
      </div>
    </nav>
  );
};

export default BottomNav;
